import { useState } from "react";
import { MessageCircle, Phone, Mail, MapPin, Clock, Send, ExternalLink } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api, openWhatsApp, track } from "@/lib/api";
import { Reveal, SectionHead } from "@/components/Reveal";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const { settings } = useSite();
  const contact = settings?.contact || {};
  const services = settings ? (settings.nav || []) : [];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = "Please enter your name";
    if (!form.phone || form.phone.replace(/\D/g, "").length < 7) errs.phone = "Please enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.message || form.message.trim().length < 5) errs.message = "Tell us a little about your project";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      await api.post("/leads", { ...form, source: "contact-form" });
      track("button_click", "/contact-submit");
      toast.success("Enquiry sent! We'll get back to you within hours.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const channels = [
    contact.whatsapp && { key: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: `+${String(contact.whatsapp).replace(/\D/g, "")}`, href: `https://wa.me/${String(contact.whatsapp).replace(/\D/g, "")}`, external: true, onClick: () => openWhatsApp(contact.whatsapp, settings?.templates?.general || "Hi!", { trackPath: "/contact" }) },
    contact.phone && { key: "phone", icon: Phone, label: "Phone", value: contact.phone, href: `tel:${String(contact.phone).replace(/\s/g, "")}`, external: false, onClick: () => {} },
    contact.email_primary && { key: "email", icon: Mail, label: "Email", value: contact.email_primary, href: `mailto:${contact.email_primary}`, external: false, onClick: () => {} },
    contact.address && { key: "address", icon: MapPin, label: "Studio", value: contact.address, href: "", external: false, onClick: () => {} },
    contact.hours && { key: "hours", icon: Clock, label: "Working Hours", value: contact.hours, href: "", external: false, onClick: () => {} },
    contact.gbp_link && { key: "gbp", icon: ExternalLink, label: "Google Business", value: "View our profile", href: contact.gbp_link, external: true, onClick: () => {} },
  ].filter(Boolean);

  const inputCls = "w-full glass rounded-xl px-4 py-3 text-sm text-white placeholder:text-subtle outline-none focus:border-accent/60 transition-colors";

  return (
    <div className="pt-24 sm:pt-28" data-testid="contact-page">
      <section className="relative py-14 sm:py-20 overflow-hidden">
        <div className="ambient-glow w-[560px] h-[300px] -top-32 left-0 opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Contact"
            title="Let's talk about your growth"
            sub="WhatsApp, call or send the form — you'll hear back from a human, fast."
          />
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5 space-y-4">
              {channels.map((c, i) => (
                <Reveal key={c.key} delay={i * 0.05}>
                  <a
                    href={c.href || undefined}
                    target={c.external ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    onClick={(e) => { if (!c.href) e.preventDefault(); c.onClick(); }}
                    className="flex items-center gap-4 glass rounded-2xl p-5 group hover:border-accent/40 transition-all duration-300 hover:-translate-y-0.5"
                    data-testid={`contact-channel-${c.key}`}
                  >
                    <span className="h-11 w-11 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110">
                      <c.icon className="h-5 w-5 text-accent-bright" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-mono-label text-[9px] text-subtle mb-1">{c.label}</p>
                      <p className="text-sm font-semibold text-white truncate">{c.value}</p>
                    </div>
                  </a>
                </Reveal>
              ))}

              {contact.maps_embed && (
                <Reveal delay={0.2}>
                  <div className="glass rounded-2xl overflow-hidden" data-testid="contact-maps-embed">
                    <iframe src={contact.maps_embed} title="Studio location" className="w-full h-56 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-10 space-y-5" data-testid="contact-lead-form" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="cf-name">Your Name *</label>
                      <input id="cf-name" className={inputCls} placeholder="Rahul Sharma" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                      {errors.name && <p className="text-xs text-rose-400 mt-1.5" data-testid="contact-name-error">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="cf-phone">Phone / WhatsApp *</label>
                      <input id="cf-phone" className={inputCls} placeholder="+91 98765 43210" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                      {errors.phone && <p className="text-xs text-rose-400 mt-1.5" data-testid="contact-phone-error">{errors.phone}</p>}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="cf-email">Email (optional)</label>
                      <input id="cf-email" type="email" className={inputCls} placeholder="you@business.com" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                      {errors.email && <p className="text-xs text-rose-400 mt-1.5" data-testid="contact-email-error">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="cf-service">Interested In</label>
                      <select id="cf-service" className={inputCls} value={form.service} onChange={set("service")} data-testid="contact-service-select">
                        <option value="" className="bg-charcoal">Select a service</option>
                        <option value="Website Development" className="bg-charcoal">Website Development</option>
                        <option value="Ad Setup & Management" className="bg-charcoal">Ad Setup & Management</option>
                        <option value="Lead Generation" className="bg-charcoal">Lead Generation</option>
                        <option value="UGC Videos" className="bg-charcoal">UGC Videos</option>
                        <option value="Not sure yet" className="bg-charcoal">Not sure yet</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-label text-[10px] text-subtle block mb-2" htmlFor="cf-message">Your Project *</label>
                    <textarea id="cf-message" rows={5} className={`${inputCls} resize-none`} placeholder="Tell us about your business and what you want to achieve..." value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                    {errors.message && <p className="text-xs text-rose-400 mt-1.5" data-testid="contact-message-error">{errors.message}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center gap-2.5 rounded-full bg-accent hover:bg-accent-bright disabled:opacity-60 text-white font-semibold px-8 py-3.5 text-sm shadow-[0_0_28px_rgba(124,58,237,0.45)] transition-all duration-300 hover:-translate-y-0.5"
                    data-testid="contact-submit-btn"
                  >
                    {sending ? "Sending..." : "Send Enquiry"}
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
